/**
 * HuddleRespondDialog — the popup users see when the HuddleJoinWidget
 * nudges them to answer a live huddle.
 *
 * R64 (Feb 2026): promoted from the old minimal max-w-lg card to the
 * SAME big layout as the /app/huddles page RespondDialog — two-column
 * grid with:
 *   • LEFT: prompts (rating / long_text / short_text) — same input shapes
 *     as before, keeps per-prompt inline suggestion checkboxes for the
 *     yesterday/today buckets.
 *   • RIGHT: a comprehensive "Since last response" checklist grouped by
 *     kind — Completed tasks, Comments, Timesheet hours, Currently open
 *     tasks — so users can compose their answer by ticking items rather
 *     than retyping from memory. Clicking a checkbox appends a "• …" line
 *     to the correct textarea (yesterday-style prompts for shipped items,
 *     today-style for planned items).
 *
 * Backend: `GET /workspaces/{ws}/huddle-instances/{iid}/pre-fill-suggestions`
 * returns { shipped: [], planned: [] } with kind in
 * {task, comment, timesheet}.
 *
 * Cmd/Ctrl+Enter submits the update.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Command, Sparkles, CheckCircle2, MessageSquare, Clock, ListTodo, ChevronDown, ChevronRight, Flame, Send, Users, HelpCircle, SkipForward, StopCircle, Eye, X } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useWorkspacePerms } from "@/lib/useWorkspacePerms";
import SmartRichText from "@/components/SmartRichText";

// Loose bucket matcher — a prompt whose key/label mentions yesterday /
// shipped is a "shipped" textarea; today / planned / doing is "planned".
function _bucketFor(prompt) {
  const key = (prompt.key || "").toLowerCase();
  const label = (prompt.label || "").toLowerCase();
  if (key.includes("yesterday") || label.includes("yesterday") || label.includes("shipped"))
    return "shipped";
  if (key.includes("today") || label.includes("today") || label.includes("doing") || label.includes("planned"))
    return "planned";
  return null;
}

const KIND_META = {
  task:      { icon: CheckCircle2, label: "Completed",       color: "text-emerald-600" },
  comment:   { icon: MessageSquare, label: "Comments",        color: "text-sky-600" },
  timesheet: { icon: Clock,         label: "Time logged",     color: "text-amber-600" },
  open_task: { icon: ListTodo,      label: "Currently open",  color: "text-violet-600" },
};

// Groups a flat suggestion list by kind, preserving order within each bucket.
function _groupByKind(items) {
  const groups = new Map();
  (items || []).forEach((it) => {
    const kind = it.kind || "task";
    if (!groups.has(kind)) groups.set(kind, []);
    groups.get(kind).push(it);
  });
  return groups;
}

// Feb 2026 · Case-insensitive filter across text / meta / project.
function _filter(items, q) {
  const needle = (q || "").trim().toLowerCase();
  if (!needle) return items || [];
  return (items || []).filter((it) => {
    const hay = [it.text, it.meta, it.project_name].filter(Boolean).join(" ").toLowerCase();
    return hay.includes(needle);
  });
}

function ChecklistSection({ title, kind, items, checked, onToggle, onToggleAll, testidPrefix }) {
  const meta = KIND_META[kind] || KIND_META.task;
  const Icon = meta.icon;
  if (!items || !items.length) return null;
  // Feb 2026 · "Select all" state — true when every item has been
  // appended, indeterminate when some are. onToggleAll flips both ways.
  const allChecked = items.every((it, i) => checked.has(it.id || `${kind}-${i}`));
  const anyChecked = items.some((it, i) => checked.has(it.id || `${kind}-${i}`));
  return (
    <div className="space-y-1" data-testid={`${testidPrefix}-section-${kind}`}>
      <div className={`flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold ${meta.color}`}>
        <Icon className="w-3 h-3" /> {title} · {items.length}
        <button
          type="button"
          onClick={() => onToggleAll?.(items, kind, !allChecked)}
          className={`ml-auto normal-case tracking-normal text-[10px] font-normal underline-offset-2 hover:underline ${allChecked ? "text-muted-foreground" : "text-primary"}`}
          data-testid={`${testidPrefix}-select-all-${kind}`}
          title={allChecked ? "Un-tick every row in this group" : "Tick every row in this group"}
        >
          {allChecked ? "Clear all" : anyChecked ? "Select rest" : "Select all"}
        </button>
      </div>
      <div className="space-y-0.5">
        {items.map((it, i) => {
          const id = it.id || `${kind}-${i}`;
          const isChecked = checked.has(id);
          return (
            <label key={id}
                   className={`flex items-start gap-2 text-xs p-1.5 border border-transparent hover:border-primary/30 hover:bg-primary/5 cursor-pointer ${isChecked ? "opacity-60" : ""}`}
                   data-testid={`${testidPrefix}-item-${kind}-${i}`}>
              <input type="checkbox" checked={isChecked}
                     onChange={() => onToggle(id, it.text)}
                     className="mt-0.5 rounded-none accent-primary" />
              <span className="flex-1 min-w-0">
                {/* Feb 2026 · User request — don't crop task names.
                    Switched from `truncate` to `break-words` so the full
                    title wraps across lines inside the checkbox row. */}
                <span className="block break-words leading-snug">{it.text}</span>
                {/* R268~6 · Feb 2026 — Project pill under each row so
                    the reader can spot cross-project context at a
                    glance even when text is truncated. */}
                {it.project_name && (
                  <span className="inline-block mt-0.5 px-1.5 py-0 text-[9px] uppercase tracking-widest bg-primary/10 text-primary border border-primary/30 shrink-0" title={`Project: ${it.project_name}`}>
                    {it.project_name}
                  </span>
                )}
                {it.meta && <span className="block text-[10px] text-muted-foreground break-words">{it.meta}</span>}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

// Feb 2026 · Group open tasks (planned bucket) by their status so the
// reader can expand "In progress" without the "Blocked" rows getting
// in the way. Falls back to a single "Open" group when items have no
// status field (older API payloads).
function _groupByStatus(items) {
  const groups = new Map();
  (items || []).forEach((it) => {
    const status = (it.status || "open").toLowerCase();
    if (!groups.has(status)) groups.set(status, []);
    groups.get(status).push(it);
  });
  return groups;
}

const STATUS_META = {
  todo:         { label: "To do",       color: "text-slate-700",   dot: "bg-slate-400" },
  open:         { label: "Open",        color: "text-slate-700",   dot: "bg-slate-400" },
  in_progress:  { label: "In progress", color: "text-sky-700",     dot: "bg-sky-500" },
  "in-progress":{ label: "In progress", color: "text-sky-700",     dot: "bg-sky-500" },
  review:       { label: "In review",   color: "text-violet-700",  dot: "bg-violet-500" },
  blocked:      { label: "Blocked",     color: "text-rose-700",    dot: "bg-rose-500" },
  on_hold:      { label: "On hold",     color: "text-amber-700",   dot: "bg-amber-500" },
  done:         { label: "Done",        color: "text-emerald-700", dot: "bg-emerald-500" },
};

export default function HuddleRespondDialog({ inst, onClose, onSubmitted }) {
  const { activeWorkspaceId } = useAuth();
  const { can, isAdminRole } = useWorkspacePerms();
  const canEndHuddle = isAdminRole || can("huddle.manage");
  const [answers, setAnswers] = useState(inst.my_answers || {});
  const [busy, setBusy] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const [ending, setEnding] = useState(false);
  const [suggestions, setSuggestions] = useState({ shipped: [], planned: [] });
  const [loading, setLoading] = useState(true);
  // Which item ids are currently appended per bucket ("shipped" / "planned").
  // Kept separate from per-prompt-key so the same item can be re-toggled
  // as the user picks a different prompt to receive the bullet.
  const [appendedByBucket, setAppendedByBucket] = useState({ shipped: new Set(), planned: new Set() });
  // Feb 2026 · Quick search string filters the pre-fill checklist across
  // both buckets. Case-insensitive match on text / meta / project.
  const [q, setQ] = useState("");
  // Feb 2026 · Collapsed state per Status group inside "Coming up". Open
  // by default so first-time viewers see everything; collapses are
  // sticky for the duration of the dialog session.
  const [collapsedStatuses, setCollapsedStatuses] = useState(() => new Set());
  // Feb 2026 · Board tab — when the user clicks a submitter's name we
  // expand their full response inline (previously the row only showed
  // the first ~90 chars).
  const [expandedBoardUid, setExpandedBoardUid] = useState(null);
  // R64.4 · Collapsible subsections on the right column (Shipped /
  // Coming up). Users can hide either side to focus.
  const [shippedOpen, setShippedOpen] = useState(true);
  const [plannedOpen, setPlannedOpen] = useState(true);
  // R64.8 · Unified with Huddles-page dialog — board / participants /
  // chat state pulled in as extra tabs on the middle column.
  const [board, setBoard] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [streaks, setStreaks] = useState([]);
  const [chat, setChat] = useState([]);
  const [chatBody, setChatBody] = useState("");
  const [midTab, setMidTab] = useState("prefill"); // prefill | board
  // Feb 2026 R283 · Manager "View as [Name]" state — when set, the
  // pre-fill middle-pane loads THAT user's data instead of the caller's.
  // Read-only; only owners/admins/facilitators/huddle.manage can flip.
  const [viewAsUid, setViewAsUid] = useState(null);
  // Feb 2026 R283 · Enriched participants (name, opened_at,
  // submitted_at, status). Populated from the new
  // /huddle-instances/{iid}/participants-detail endpoint.
  const [participantsDetail, setParticipantsDetail] = useState([]);
  // Drawer opened via the eye icon on a participant row.
  const [drawerUid, setDrawerUid] = useState(null);
  const [drawerData, setDrawerData] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);

  // Load participants + streaks once per huddle.
  useEffect(() => {
    if (!inst?.huddle_id) return;
    api.get(`/huddles/${inst.huddle_id}/participants`)
       .then(({ data }) => setParticipants(data || [])).catch(() => {});
    api.get(`/huddles/${inst.huddle_id}/streaks`)
       .then(({ data }) => setStreaks(data || [])).catch(() => {});
  }, [inst?.huddle_id]);

  // Feb 2026 R283 · Stamp `opened_at` on THIS user's response the first
  // time they open the dialog. Idempotent server-side so re-opens are
  // no-ops. Fire and forget — never blocks the dialog from rendering.
  useEffect(() => {
    if (!inst?.instance_id) return;
    api.post(`/huddle-instances/${inst.instance_id}/mark-opened`).catch(() => {});
  }, [inst?.instance_id]);

  // Feb 2026 R283 · Load enriched participants (with opened_at) alongside
  // the regular participants list. Refreshed with the same 10s cadence
  // as the board so "Opened HH:MM" chips appear near-real-time.
  const loadParticipantsDetail = useCallback(async () => {
    if (!inst?.instance_id) return;
    try {
      const { data } = await api.get(`/huddle-instances/${inst.instance_id}/participants-detail`);
      setParticipantsDetail(Array.isArray(data) ? data : []);
    } catch { /* noop */ }
  }, [inst?.instance_id]);
  useEffect(() => {
    if (!inst?.instance_id) return undefined;
    loadParticipantsDetail();
    const iv = setInterval(() => {
      if (document.visibilityState === "visible") loadParticipantsDetail();
    }, 30000);
    return () => clearInterval(iv);
  }, [inst?.instance_id, loadParticipantsDetail]);

  // Poll board + chat every 10s while the tab is visible.
  const loadBoardAndChat = useCallback(async () => {
    if (!inst?.instance_id) return;
    try {
      const [b, c] = await Promise.all([
        api.get(`/huddle-instances/${inst.instance_id}`).catch(() => ({ data: null })),
        api.get(`/huddle-instances/${inst.instance_id}/chat`).catch(() => ({ data: [] })),
      ]);
      if (b?.data) setBoard(b.data);
      setChat(Array.isArray(c?.data) ? c.data : []);
    } catch { /* noop */ }
  }, [inst?.instance_id]);

  useEffect(() => {
    if (!inst?.instance_id) return undefined;
    loadBoardAndChat();
    const iv = setInterval(() => {
      if (document.visibilityState === "visible") loadBoardAndChat();
    }, 10000);
    return () => clearInterval(iv);
  }, [inst?.instance_id, loadBoardAndChat]);

  const streakByUser = useMemo(() => {
    const m = new Map();
    streaks.forEach((s) => m.set(s.user_id, s));
    return m;
  }, [streaks]);
  const submittedIds = useMemo(() => new Set(
    (board?.responses || []).filter((r) => r.status === "submitted").map((r) => r.user_id),
  ), [board]);

  const sendChat = async () => {
    if (!chatBody.trim()) return;
    try {
      await api.post(`/huddle-instances/${inst.instance_id}/chat`, { body: chatBody.trim() });
      setChatBody("");
      loadBoardAndChat();
    } catch (e) { toast.error(apiErrorMessage(e, "Chat send failed")); }
  };

  useEffect(() => {
    if (!activeWorkspaceId || !inst?.instance_id) return;
    setLoading(true);
    // Feb 2026 R283 · If viewing as another user (manager mode), hit
    // the read-only per-user endpoint. Otherwise use the caller's own
    // pre-fill endpoint.
    const url = viewAsUid
      ? `/huddle-instances/${inst.instance_id}/pre-fill-suggestions-for/${viewAsUid}`
      : `/workspaces/${activeWorkspaceId}/huddle-instances/${inst.instance_id}/pre-fill-suggestions`;
    api.get(url)
       .then((r) => setSuggestions({
         shipped: r.data?.shipped || [],
         planned: r.data?.planned || [],
       }))
       .catch(() => {})
       .finally(() => setLoading(false));
  }, [activeWorkspaceId, inst?.instance_id, viewAsUid]);

  // Load per-participant pre-fill data on demand for the side drawer.
  useEffect(() => {
    if (!drawerUid || !inst?.instance_id) { setDrawerData(null); return; }
    setDrawerLoading(true);
    api.get(`/huddle-instances/${inst.instance_id}/pre-fill-suggestions-for/${drawerUid}`)
       .then((r) => setDrawerData({
         shipped: r.data?.shipped || [],
         planned: r.data?.planned || [],
       }))
       .catch(() => setDrawerData({ shipped: [], planned: [], error: true }))
       .finally(() => setDrawerLoading(false));
  }, [drawerUid, inst?.instance_id]);

  // Find the first prompt in each bucket so the "append to prompt" action
  // has a deterministic target. If no matching prompt exists, we fall
  // back to the first long_text prompt.
  const promptForBucket = useMemo(() => {
    const prompts = inst.prompts || [];
    const long = prompts.filter((p) => p.kind === "long_text");
    const findByBucket = (b) => long.find((p) => _bucketFor(p) === b) || null;
    return {
      shipped: findByBucket("shipped") || long[0] || null,
      planned: findByBucket("planned") || long[1] || long[0] || null,
    };
  }, [inst.prompts]);

  const onToggleItem = (bucket, itemId, itemText) => {
    const target = promptForBucket[bucket];
    if (!target) {
      toast.error(`No ${bucket === "shipped" ? "yesterday" : "today"}-style prompt to append to`);
      return;
    }
    const promptKey = target.key;
    const already = appendedByBucket[bucket].has(itemId);
    // Compute the toggle intent first — Strict-Mode-safe.
    if (already) {
      setAppendedByBucket((prev) => {
        const next = new Set(prev[bucket]);
        next.delete(itemId);
        return { ...prev, [bucket]: next };
      });
      setAnswers((a) => {
        const current = a[promptKey] || "";
        const stripped = current
          .split("\n")
          .filter((ln) => ln.replace(/^[\s•\-]+/, "").trim() !== itemText.trim())
          .join("\n");
        return { ...a, [promptKey]: stripped };
      });
    } else {
      setAppendedByBucket((prev) => {
        if (prev[bucket].has(itemId)) return prev;
        const next = new Set(prev[bucket]);
        next.add(itemId);
        return { ...prev, [bucket]: next };
      });
      setAnswers((a) => {
        const current = (a[promptKey] || "").trimEnd();
        const line = `• ${itemText}`;
        if (current.split("\n").some((ln) => ln.trim() === line.trim())) return a;
        return { ...a, [promptKey]: current ? `${current}\n${line}` : line };
      });
    }
  };

  // Feb 2026 · Bulk toggle for a ChecklistSection — flips every item in
  // the group to the requested state in one go. Reuses onToggleItem so
  // append/strip logic stays single-sourced.
  const onToggleAllInGroup = (bucket) => (items, kind, select) => {
    (items || []).forEach((it, i) => {
      const id = it.id || `${kind}-${i}`;
      const isChecked = appendedByBucket[bucket].has(id);
      if (select && !isChecked) onToggleItem(bucket, id, it.text);
      else if (!select && isChecked) onToggleItem(bucket, id, it.text);
    });
  };

  const submit = useCallback(async () => {
    setBusy(true);
    try {
      await api.post(`/huddle-instances/${inst.instance_id}/submit`, { answers });
      toast.success("Response saved");
      onSubmitted();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  }, [answers, inst.instance_id, onSubmitted]);

  // R123 · Feb 2026 — Skip huddle (mark my response as non-attendance).
  const doSkip = useCallback(async () => {
    if (!window.confirm("Skip this huddle? It will be marked as non-attendance for today.")) return;
    setSkipping(true);
    try {
      await api.post(`/huddle-instances/${inst.instance_id}/skip`, { reason: null });
      toast.success("Huddle skipped for today");
      onSubmitted();
    } catch (e) { toast.error(apiErrorMessage(e, "Skip failed")); }
    finally { setSkipping(false); }
  }, [inst.instance_id, onSubmitted]);

  // R123 · Feb 2026 — Manager ends the huddle early (freezes responses).
  const doEnd = useCallback(async () => {
    if (!window.confirm("End huddle now? Nobody else will be able to submit or skip after this.")) return;
    setEnding(true);
    try {
      await api.post(`/huddle-instances/${inst.instance_id}/close`);
      toast.success("Huddle ended");
      onSubmitted();
    } catch (e) { toast.error(apiErrorMessage(e, "End failed")); }
    finally { setEnding(false); }
  }, [inst.instance_id, onSubmitted]);

  // Cmd/Ctrl+Enter submits.
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        if (!busy) submit();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [submit, busy]);

  const shippedGroups = useMemo(() => _groupByKind(_filter(suggestions.shipped, q)), [suggestions.shipped, q]);
  const plannedGroups = useMemo(() => _groupByKind(_filter(suggestions.planned, q)), [suggestions.planned, q]);
  const plannedTaskByStatus = useMemo(
    () => _groupByStatus(plannedGroups.get("task") || []),
    [plannedGroups],
  );
  const hasAnySuggestions = suggestions.shipped.length + suggestions.planned.length > 0;

  const _fmtInstant = (v) => {
    if (!v) return null;
    try {
      const d = new Date(v);
      return d.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return String(v).slice(0, 16).replace("T", " "); }
  };
  const scheduledAt = _fmtInstant(inst.scheduled_at || inst.start_at || inst.instance_date);

  // R64.9 · Partition prompts — rating prompts (typically "confidence")
  // are hoisted into the header alongside the Submit button so users
  // can rate + submit without hunting through the LEFT column. Long
  // and short text prompts stay in the LEFT column.
  const ratingPrompts = (inst.prompts || []).filter((p) => p.kind === "rating");
  const bodyPrompts   = (inst.prompts || []).filter((p) => p.kind !== "rating");
  const _confidenceHelp = "1 = blocked or unsure · 2 = risky · 3 = on track · 4 = confident · 5 = very confident, no issues";

  return (
    <Dialog open onOpenChange={onClose}>
      {/* Feb 2026 R283 · Full-screen respond dialog per user request.
           Was `max-w-6xl w-[95vw] max-h-[82vh]` — bumped to full
           viewport so the middle "Since last update" pane + board
           have room to breathe on 13-inch laptops. */}
      <DialogContent className="max-w-none w-screen h-screen max-h-screen rounded-none overflow-y-auto p-0 sm:max-w-none sm:rounded-none" data-testid="huddle-respond-dialog">
        <DialogHeader className="px-4 py-2 border-b border-border bg-gradient-to-r from-primary/10 via-transparent to-transparent pr-12">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <DialogTitle className="text-base leading-tight flex items-center gap-2 flex-wrap">
                <Sparkles className="w-4 h-4 text-primary shrink-0" />
                <span className="truncate">{inst.huddle_name}</span>
                {inst.cadence && (
                  <span className="text-[9px] uppercase tracking-widest font-semibold px-1.5 py-0.5 border border-primary/40 bg-primary/5 text-primary rounded-none">
                    {inst.cadence}
                  </span>
                )}
                {inst.status && (
                  <span className="text-[9px] uppercase tracking-widest font-semibold px-1.5 py-0.5 border border-border bg-muted text-muted-foreground rounded-none">
                    {inst.status}
                  </span>
                )}
              </DialogTitle>
              <DialogDescription className="mt-0.5 text-[11px] flex items-center gap-2 flex-wrap">
                {scheduledAt && (
                  <span className="inline-flex items-center gap-1 text-muted-foreground">
                    <Clock className="w-3 h-3" />{scheduledAt}
                  </span>
                )}
                {inst.participants_count != null && (
                  <span className="inline-flex items-center gap-1 text-muted-foreground">
                    <ListTodo className="w-3 h-3" />{inst.participants_count} participants
                  </span>
                )}
                <span className="text-muted-foreground">·</span>
                <span className="inline-flex items-center gap-1 text-muted-foreground">
                  <Command className="w-3 h-3" /> + Enter to submit
                </span>
              </DialogDescription>
            </div>
          </div>
          {/* R64.9 · Header controls row — rating prompts (Confidence)
               + Submit button hoisted here so users don't have to scroll
               the LEFT column to submit. Only rendered when the huddle
               actually has rating prompts. */}
          {(ratingPrompts.length > 0 || bodyPrompts.length > 0) && (
            <div className="flex items-center gap-3 mt-2 pt-2 border-t border-border/60 flex-wrap">
              {ratingPrompts.map((p) => (
                <div key={p.key} className="flex items-center gap-2" data-testid={`huddle-header-rating-${p.key}`}>
                  <span className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">{p.label}</span>
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button key={n} type="button"
                              onClick={() => setAnswers((prev) => ({ ...prev, [p.key]: n }))}
                              className={`w-7 h-7 border text-xs font-semibold transition-colors ${Number(answers[p.key]) === n ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary/60"}`}
                              data-testid={`huddle-widget-rating-${p.key}-${n}`}>{n}</button>
                    ))}
                  </div>
                  <span className="inline-flex items-center gap-0.5 text-muted-foreground cursor-help"
                        title={_confidenceHelp}
                        data-testid={`huddle-header-rating-help-${p.key}`}>
                    <HelpCircle className="w-3 h-3" />
                  </span>
                </div>
              ))}
              <Button onClick={submit} disabled={busy || skipping || ending || inst.status === "closed"} size="sm"
                      className="rounded-none h-8 text-xs px-4 ml-auto"
                      data-testid="huddle-widget-submit">
                {busy ? "Saving…" : "Submit update"}
              </Button>
              <Button onClick={doSkip} disabled={busy || skipping || ending || inst.status === "closed"}
                      variant="outline" size="sm"
                      className="rounded-none h-8 text-xs px-3"
                      title="Mark today as non-attendance for this huddle"
                      data-testid="huddle-widget-skip">
                <SkipForward className="w-3 h-3 mr-1" />{skipping ? "Skipping…" : "Skip huddle"}
              </Button>
              {canEndHuddle && (
                <Button onClick={doEnd} disabled={busy || skipping || ending || inst.status === "closed"}
                        variant="outline" size="sm"
                        className="rounded-none h-8 text-xs px-3 border-red-500/60 text-red-600 hover:bg-red-50"
                        title="Freeze all responses — nobody can submit or skip after this"
                        data-testid="huddle-widget-end">
                  <StopCircle className="w-3 h-3 mr-1" />{ending ? "Ending…" : (inst.status === "closed" ? "Ended" : "End huddle")}
                </Button>
              )}
            </div>
          )}
        </DialogHeader>

        <div className="grid grid-cols-12 gap-3 px-4 py-3">
          {/* LEFT — long/short-text prompts (sticky within the scrolling
              dialog body so users can pin their update while browsing
              the checklist). Rating prompts moved into the header.
              Feb 2026 R283 · max-h bumped for full-screen. */}
          <div className="col-span-12 md:col-span-5 space-y-2 md:sticky md:top-2 md:self-start md:max-h-[calc(100vh-160px)] md:overflow-y-auto md:pr-1"
               data-testid="huddle-left-column">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Your update</div>
            {bodyPrompts.map((p) => (
              <div key={p.key} className="border border-border p-2 space-y-1.5 hover:border-primary/40 transition-colors">
                <Label className="label-eyebrow text-[10px]">{p.label}</Label>
                {p.kind === "long_text" ? (
                  /* R64.9 · Rich text editor with auto-grow. `minRows=2`
                       gives a short default; `maxHeight={280}` caps the
                       auto-grow, then the LEFT column wrapper's
                       `overflow-y-auto` takes over. */
                  <SmartRichText
                    value={answers[p.key] || ""}
                    onChange={(html) => setAnswers((prev) => ({ ...prev, [p.key]: html }))}
                    placeholder="Type here or tick items on the right →"
                    minRows={2}
                    maxHeight={280}
                    workspaceId={activeWorkspaceId}
                    mediaMode="url-only"
                    testid={`huddle-widget-answer-${p.key}`}
                  />
                ) : (
                  <Input value={answers[p.key] || ""} onChange={(e) => setAnswers((prev) => ({ ...prev, [p.key]: e.target.value }))} className="h-9 rounded-none" data-testid={`huddle-widget-answer-${p.key}`} />
                )}
              </div>
            ))}
          </div>

          {/* MIDDLE — Tabs: Pre-fill checklist | Live board.
              R64.8 · Unified from the /app/huddles RespondDialog.
              Feb 2026 R283 · max-h bumped for full-screen. */}
          <div className="col-span-12 md:col-span-4 border-t md:border-t-0 md:border-l border-border md:pl-3 pt-3 md:pt-0 md:max-h-[calc(100vh-160px)] md:overflow-y-auto"
               data-testid="huddle-prefill-panel">
            {/* Feb 2026 R283 · Manager-only "View as [Name]" selector.
                 Only rendered for owners/admins/facilitators (server
                 enforces the same rule on the endpoint). Selecting a
                 name swaps the middle-pane data to that user's view. */}
            {canEndHuddle && participants.length > 0 && (
              <div className="mb-2 flex items-center gap-1.5 text-[10px]"
                   data-testid="huddle-view-as-row">
                <span className="uppercase tracking-widest text-muted-foreground font-semibold">View as</span>
                <select
                  value={viewAsUid || ""}
                  onChange={(e) => setViewAsUid(e.target.value || null)}
                  className="h-6 border border-border bg-background rounded-none text-[10px] px-1 flex-1 min-w-0"
                  data-testid="huddle-view-as-select"
                >
                  <option value="">Myself</option>
                  {participants.map((p) => (
                    <option key={p.id} value={p.id}>{p.name || p.email || p.id.slice(0, 8)}</option>
                  ))}
                </select>
                {viewAsUid && (
                  <button
                    type="button"
                    onClick={() => setViewAsUid(null)}
                    className="text-[10px] text-rose-600 underline hover:text-rose-700"
                    data-testid="huddle-view-as-clear"
                  >clear</button>
                )}
              </div>
            )}
            {viewAsUid && (
              <div className="mb-2 border border-amber-300 bg-amber-50 px-2 py-1 text-[10px] text-amber-900" data-testid="huddle-view-as-banner">
                Read-only preview of <b>{(participants.find((p) => p.id === viewAsUid) || {}).name || "another user"}</b>'s middle pane. Your own submission below is untouched.
              </div>
            )}
            <Tabs value={midTab} onValueChange={setMidTab} className="sticky top-0 bg-background z-10">
              <TabsList className="rounded-none h-7 grid grid-cols-2 w-full">
                <TabsTrigger value="prefill" className="rounded-none text-[10px] uppercase tracking-widest" data-testid="huddle-tab-prefill">
                  <Sparkles className="w-3 h-3 mr-1" />Since last · {suggestions.shipped.length + suggestions.planned.length}
                </TabsTrigger>
                <TabsTrigger value="board" className="rounded-none text-[10px] uppercase tracking-widest" data-testid="huddle-tab-board">
                  <CheckCircle2 className="w-3 h-3 mr-1" />Board {board?.responses ? `· ${board.responses.filter((r) => r.status === "submitted").length}` : ""}
                </TabsTrigger>
              </TabsList>
            </Tabs>
            {midTab === "prefill" && (
            <div className="space-y-2 pt-2">
            {/* Feb 2026 · Quick search across both buckets so a user with
                 30+ completed tasks can zero in on the one they want to
                 mention without scrolling. Also hides the empty-group
                 headers automatically because ChecklistSection
                 short-circuits on empty items. */}
            {hasAnySuggestions && (
              <div className="relative" data-testid="huddle-prefill-search-wrap">
                <input
                  type="search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search tasks, comments, projects…"
                  className="w-full h-7 rounded-none border border-border bg-background px-2 text-[11px] focus:border-primary focus:outline-none"
                  data-testid="huddle-prefill-search"
                />
                {q && (
                  <button type="button"
                          onClick={() => setQ("")}
                          className="absolute right-1 top-0.5 text-[10px] text-muted-foreground hover:text-foreground px-1"
                          data-testid="huddle-prefill-search-clear">clear</button>
                )}
              </div>
            )}
            {loading && <div className="text-[11px] text-muted-foreground italic">Loading suggestions…</div>}
            {!loading && !hasAnySuggestions && (
              <div className="text-[11px] text-muted-foreground italic border border-dashed border-border p-2" data-testid="huddle-prefill-empty">
                Nothing to pre-fill — no completed tasks, comments, or time logged since your last response.
              </div>
            )}
            {suggestions.shipped.length > 0 && (
              <div className="border-l-2 border-emerald-500/40 pl-3" data-testid="huddle-prefill-shipped">
                <button type="button"
                        onClick={() => setShippedOpen((v) => !v)}
                        className="w-full flex items-center justify-between gap-2 py-1"
                        data-testid="huddle-prefill-shipped-toggle">
                  <span className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold text-left">
                    Shipped · {suggestions.shipped.length} · appends to <span className="font-mono">{promptForBucket.shipped?.label || "(no prompt)"}</span>
                  </span>
                  {shippedOpen ? <ChevronDown className="w-3 h-3 text-emerald-700" /> : <ChevronRight className="w-3 h-3 text-emerald-700" />}
                </button>
                {shippedOpen && (
                  <div className="space-y-3 pt-2">
                    <ChecklistSection title="Completed tasks" kind="task"
                                        items={shippedGroups.get("task")}
                                        checked={appendedByBucket.shipped}
                                        onToggle={(id, text) => onToggleItem("shipped", id, text)}
                                        onToggleAll={onToggleAllInGroup("shipped")}
                                        testidPrefix="huddle-prefill-shipped" />
                    <ChecklistSection title="Comments" kind="comment"
                                        items={shippedGroups.get("comment")}
                                        checked={appendedByBucket.shipped}
                                        onToggle={(id, text) => onToggleItem("shipped", id, text)}
                                        onToggleAll={onToggleAllInGroup("shipped")}
                                        testidPrefix="huddle-prefill-shipped" />
                    <ChecklistSection title="Time logged" kind="timesheet"
                                        items={shippedGroups.get("timesheet")}
                                        checked={appendedByBucket.shipped}
                                        onToggle={(id, text) => onToggleItem("shipped", id, text)}
                                        onToggleAll={onToggleAllInGroup("shipped")}
                                        testidPrefix="huddle-prefill-shipped" />
                  </div>
                )}
              </div>
            )}
            {suggestions.planned.length > 0 && (
              <div className="border-l-2 border-violet-500/40 pl-3" data-testid="huddle-prefill-planned">
                <button type="button"
                        onClick={() => setPlannedOpen((v) => !v)}
                        className="w-full flex items-center justify-between gap-2 py-1"
                        data-testid="huddle-prefill-planned-toggle">
                  <span className="text-[10px] uppercase tracking-widest text-violet-700 font-bold text-left">
                    Coming up · {suggestions.planned.length} · appends to <span className="font-mono">{promptForBucket.planned?.label || "(no prompt)"}</span>
                  </span>
                  {plannedOpen ? <ChevronDown className="w-3 h-3 text-violet-700" /> : <ChevronRight className="w-3 h-3 text-violet-700" />}
                </button>
                {plannedOpen && (
                  <div className="space-y-3 pt-2">
                    {/* Feb 2026 · Open tasks are now subgrouped by their
                         board status so the reader can collapse "Done"
                         or focus on "Blocked". Each sub-group has its own
                         Select-all. Non-task kinds remain flat. */}
                    {Array.from(plannedTaskByStatus.entries()).map(([status, items]) => {
                      const sm = STATUS_META[status] || { label: status.replace(/_/g, " "), color: "text-slate-700", dot: "bg-slate-400" };
                      const sKey = `planned:${status}`;
                      const open = !collapsedStatuses.has(sKey);
                      return (
                        <div key={status} className="border border-border/60 rounded-none" data-testid={`huddle-prefill-planned-status-${status}`}>
                          <button
                            type="button"
                            onClick={() => setCollapsedStatuses((prev) => {
                              const next = new Set(prev);
                              if (next.has(sKey)) next.delete(sKey); else next.add(sKey);
                              return next;
                            })}
                            className="w-full flex items-center gap-2 px-2 py-1 bg-muted/30"
                            data-testid={`huddle-prefill-planned-status-toggle-${status}`}
                          >
                            {open ? <ChevronDown className="w-3 h-3 text-muted-foreground" /> : <ChevronRight className="w-3 h-3 text-muted-foreground" />}
                            <span className={`w-1.5 h-1.5 rounded-full ${sm.dot}`} />
                            <span className={`text-[10px] uppercase tracking-widest font-semibold ${sm.color}`}>
                              {sm.label} · {items.length}
                            </span>
                          </button>
                          {open && (
                            <div className="p-1">
                              <ChecklistSection title={sm.label} kind="open_task"
                                                items={items}
                                                checked={appendedByBucket.planned}
                                                onToggle={(id, text) => onToggleItem("planned", id, text)}
                                                onToggleAll={onToggleAllInGroup("planned")}
                                                testidPrefix={`huddle-prefill-planned-${status}`} />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            </div>
            )}
            {midTab === "board" && (
              <div className="space-y-2 pt-2" data-testid="huddle-board-tab">
                {!board && <div className="text-[11px] text-muted-foreground italic">Loading board…</div>}
                {board && (
                  <>
                    <div className="grid grid-cols-4 gap-1 text-center">
                      {Object.entries(board.ai?.totals || {}).map(([k, v]) => (
                        <div key={k} className="border border-border p-1.5" data-testid={`huddle-board-total-${k}`}>
                          <div className="text-[8px] uppercase tracking-widest text-muted-foreground truncate">{k}</div>
                          <div className="text-sm font-bold">{v}</div>
                        </div>
                      ))}
                    </div>
                    {board.ai?.confidence_avg != null && (
                      <div className="text-[11px]">Avg confidence: <b>{board.ai.confidence_avg}</b> / 5</div>
                    )}
                    {board.ai?.ai_summary && (
                      <div className="border border-primary/30 bg-primary/5 p-2 text-[11px]" data-testid="huddle-board-ai">
                        <div className="text-[9px] uppercase tracking-widest text-primary mb-1"><Sparkles className="w-3 h-3 inline mr-1" /> AI</div>
                        {board.ai.ai_summary}
                      </div>
                    )}
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-2">Recent responses</div>
                    <ul className="space-y-1 text-[11px]" data-testid="huddle-board-recent">
                      {(board.responses || []).filter((r) => r.status === "submitted").slice(0, 20).map((r) => {
                        const open = expandedBoardUid === r.user_id;
                        return (
                          <li key={r.id} className="border-l-2 border-emerald-500 pl-2" data-testid={`huddle-board-resp-${r.user_id}`}>
                            <button
                              type="button"
                              className="w-full text-left hover:bg-primary/5 py-0.5"
                              onClick={() => setExpandedBoardUid(open ? null : r.user_id)}
                              data-testid={`huddle-board-resp-toggle-${r.user_id}`}
                              title={open ? "Hide full response" : "Click to view the full response"}
                            >
                              <div className="flex items-center gap-1">
                                <b className="text-primary underline-offset-2 hover:underline">{r.user_name}</b>
                                {open ? <ChevronDown className="w-3 h-3 text-muted-foreground" /> : <ChevronRight className="w-3 h-3 text-muted-foreground" />}
                              </div>
                              {!open && (
                                <div className="text-muted-foreground text-[10px] truncate">
                                  {String(r.answers?.today || r.answers?.yesterday || "").replace(/<[^>]+>/g, "").slice(0, 90)}
                                </div>
                              )}
                            </button>
                            {open && (
                              <div className="pt-1 pb-2 space-y-2" data-testid={`huddle-board-resp-full-${r.user_id}`}>
                                {Object.entries(r.answers || {}).map(([k, v]) => {
                                  if (v == null || v === "") return null;
                                  const text = typeof v === "string" ? v.replace(/<[^>]+>/g, "\n") : String(v);
                                  return (
                                    <div key={k} className="border border-border/60 p-1.5 bg-muted/20">
                                      <div className="text-[9px] uppercase tracking-widest text-muted-foreground mb-0.5">{k}</div>
                                      <div className="whitespace-pre-wrap break-words text-[11px]">{text}</div>
                                    </div>
                                  );
                                })}
                                {Object.keys(r.answers || {}).length === 0 && (
                                  <div className="text-[10px] italic text-muted-foreground">No answer fields.</div>
                                )}
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </>
                )}
              </div>
            )}
          </div>

          {/* RIGHT — Participants + Chat rail
              R64.8 · Unified from the /app/huddles RespondDialog.
              Feb 2026 R283 · max-h bumped for full-screen. */}
          <div className="col-span-12 md:col-span-3 border-t md:border-t-0 md:border-l border-border md:pl-3 pt-3 md:pt-0 flex flex-col md:max-h-[calc(100vh-160px)]"
               data-testid="huddle-chat-panel">
            <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1">
              <Users className="w-3 h-3" /> Participants
              <span className="normal-case text-[9px] font-normal">({participants.length})</span>
            </div>
            <ul className="border border-border max-h-64 overflow-y-auto divide-y divide-border" data-testid="huddle-participants-list">
              {participants.length === 0 && <li className="p-2 text-[10px] italic text-muted-foreground">No expected participants.</li>}
              {participants.map((p) => {
                const s = streakByUser.get(p.id);
                const live = submittedIds.has(p.id);
                // Feb 2026 R283 · Merge in `opened_at` from the
                // participants-detail response so we can show the
                // "Opened HH:MM" chip.
                const detail = participantsDetail.find((d) => d.user_id === p.id) || {};
                const openedAt = detail.opened_at;
                return (
                  <li key={p.id} className="flex items-center gap-1.5 px-1.5 py-1 text-[10px]" data-testid={`huddle-participant-${p.id}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${live ? "bg-emerald-500" : "bg-muted-foreground/30"}`}
                          title={live ? "Submitted today" : "Not submitted yet"} />
                    <span className="flex-1 truncate">{p.name || p.email || p.id.slice(0, 8)}</span>
                    {openedAt && !live && (
                      <span
                        className="inline-flex items-center gap-0.5 text-[9px] text-sky-700 border border-sky-300 bg-sky-50 px-1"
                        title={`First opened the huddle at ${new Date(openedAt).toLocaleString()}`}
                        data-testid={`huddle-participant-opened-${p.id}`}
                      >
                        <Eye className="w-2.5 h-2.5" /> {new Date(openedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </span>
                    )}
                    {s?.current > 0 && (
                      <span className="inline-flex items-center gap-0.5 text-[9px] text-amber-700 border border-amber-300 px-1"
                            title={`Current streak · longest ${s.longest || 0}`}>
                        <Flame className="w-2.5 h-2.5" /> {s.current}
                      </span>
                    )}
                    {canEndHuddle && (
                      <button
                        type="button"
                        onClick={() => setDrawerUid(p.id)}
                        className="inline-flex items-center text-muted-foreground hover:text-primary"
                        title="View this person's data (read-only)"
                        data-testid={`huddle-participant-view-${p.id}`}
                      >
                        <Eye className="w-3 h-3" />
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
            <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mt-2 mb-1">
              <MessageSquare className="w-3 h-3" /> Chat
              <span className="normal-case text-[9px] font-normal">({chat.length})</span>
            </div>
            <div className="flex-1 min-h-[80px] max-h-56 overflow-y-auto space-y-1 border border-border p-2" data-testid="huddle-chat-messages">
              {chat.length === 0 && <div className="text-[10px] italic text-muted-foreground">No messages yet — say hi.</div>}
              {chat.map((m) => (
                <div key={m.id} className="text-[10px]" data-testid={`huddle-chat-msg-${m.id}`}>
                  <b>{m.user_name}</b>
                  <span className="text-muted-foreground ml-1">{(m.created_at || "").slice(11, 16)}</span>
                  <div className="whitespace-pre-wrap break-words">{m.body}</div>
                </div>
              ))}
            </div>
            <div className="flex gap-1 mt-1.5">
              <Input value={chatBody}
                     onChange={(e) => setChatBody(e.target.value)}
                     onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && !e.metaKey && !e.ctrlKey) { e.preventDefault(); sendChat(); } }}
                     placeholder="Type a message…"
                     className="h-7 rounded-none text-xs"
                     data-testid="huddle-chat-input" />
              <Button onClick={sendChat}
                      size="icon"
                      className="rounded-none h-7 w-7 shrink-0"
                      data-testid="huddle-chat-send"
                      title="Send message"><Send className="w-3 h-3" /></Button>
            </div>
          </div>
        </div>

        {/* Feb 2026 R283 · Side drawer — opens when a manager clicks the
             eye icon on any participant. Shows that user's Since-last-
             update + planned lists (read-only). Server enforces the
             permission gate on the endpoint. */}
        {drawerUid && (
          <div
            className="fixed inset-0 z-[85] bg-black/50 flex items-stretch justify-end"
            onClick={() => setDrawerUid(null)}
            data-testid="huddle-participant-drawer"
          >
            <div
              className="bg-background w-full sm:w-[440px] h-full border-l-4 border-primary overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 z-10 bg-background border-b border-border px-4 py-2 flex items-center gap-2">
                <Eye className="w-4 h-4 text-primary" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-bold truncate">
                    {(participants.find((p) => p.id === drawerUid) || {}).name || "Participant"}
                  </div>
                  <div className="text-[10px] text-muted-foreground">Read-only preview of their middle pane</div>
                </div>
                <button
                  type="button"
                  onClick={() => setDrawerUid(null)}
                  className="p-1 hover:bg-muted rounded-none"
                  data-testid="huddle-participant-drawer-close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="p-3 space-y-3">
                {drawerLoading && (
                  <div className="text-[11px] text-muted-foreground italic">Loading…</div>
                )}
                {drawerData?.error && (
                  <div className="border border-rose-300 bg-rose-50 p-2 text-[11px] text-rose-800">
                    Couldn't load — permission denied or endpoint error.
                  </div>
                )}
                {!drawerLoading && drawerData && !drawerData.error && (
                  <>
                    <div>
                      <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-1">
                        <Sparkles className="w-3 h-3" /> Since last update ({drawerData.shipped.length})
                      </div>
                      {drawerData.shipped.length === 0 ? (
                        <div className="text-[11px] text-muted-foreground italic border border-dashed border-border p-2">Nothing yet.</div>
                      ) : (
                        <ul className="border border-border divide-y divide-border text-[11px]">
                          {drawerData.shipped.map((it) => (
                            <li key={it.id} className="px-2 py-1"
                                   data-testid={`drawer-shipped-${it.id}`}>
                              <div>{it.text}</div>
                              {(it.project_name || it.meta) && (
                                <div className="text-[9px] text-muted-foreground mt-0.5">
                                  {it.project_name ? <b>{it.project_name}</b> : null}
                                  {it.project_name && it.meta ? " · " : ""}
                                  {it.meta}
                                </div>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div>
                      <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-1">
                        <ListTodo className="w-3 h-3" /> Coming up ({drawerData.planned.length})
                      </div>
                      {drawerData.planned.length === 0 ? (
                        <div className="text-[11px] text-muted-foreground italic border border-dashed border-border p-2">Nothing planned.</div>
                      ) : (
                        <ul className="border border-border divide-y divide-border text-[11px]">
                          {drawerData.planned.map((it) => (
                            <li key={it.id} className="px-2 py-1"
                                   data-testid={`drawer-planned-${it.id}`}>
                              <div>{it.text}</div>
                              {(it.project_name || it.meta) && (
                                <div className="text-[9px] text-muted-foreground mt-0.5">
                                  {it.project_name ? <b>{it.project_name}</b> : null}
                                  {it.project_name && it.meta ? " · " : ""}
                                  {it.meta}
                                </div>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
